import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { fmtINR } from "./_shared";

// Scope toggle — Active (default) excludes won/lost; All = entire dataset.
const SCOPE_OPTS = [
  { v: "active", l: "Active" },
  { v: "all",    l: "All" },
];

// Liquidity bucket palette — green (deep liquid) → red (illiquid).
const LIQ_BUCKET_COLORS = {
  "9-10": "#22C55E",
  "7-8":  "#84CC16",
  "5-6":  "#F5A623",
  "3-4":  "#FF8C42",
  "1-2":  "#FF1E1E",
};
// Gap bucket palette — green (tight/positive) → red (large negative gap).
const GAP_BUCKET_COLORS = {
  "Positive Gap": "#22C55E",
  "0% to -5%":    "#84CC16",
  "-5% to -10%":  "#F5A623",
  "-10% to -15%": "#FF8C42",
  "-15% to -20%": "#FF6B1A",
  "Below -20%":   "#FF1E1E",
};

function fmtPct(n) {
  if (n === null || n === undefined) return "—";
  return `${Number(n).toFixed(1)}%`;
}

function liquidityBadgeClass(liq) {
  if (liq === null || liq === undefined) return "text-white/40";
  if (liq >= 9) return "text-emerald-400";
  if (liq >= 7) return "text-lime-400";
  if (liq >= 5) return "text-amber-400";
  if (liq >= 3) return "text-orange-400";
  return "text-[#FF1E1E]";
}

function gapClass(g) {
  if (g === null || g === undefined) return "text-white/40";
  if (g > 0) return "text-emerald-400";
  if (g >= -5) return "text-lime-400";
  if (g >= -10) return "text-amber-400";
  if (g >= -15) return "text-orange-400";
  return "text-[#FF1E1E]";
}

function BarTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0].payload;
  return (
    <div className="bg-[#0E0E0E] border border-[#2A2A2A] px-3 py-2 text-xs font-mono-data">
      <div className="text-white font-display normal-case mb-1">{label}</div>
      <div className="text-white/70">Count: <span className="text-white">{row.count}</span></div>
      {row.avg_gap_pct !== undefined && row.avg_gap_pct !== null && (
        <div className="text-white/70">Avg Gap: <span className="text-white">{fmtPct(row.avg_gap_pct)}</span></div>
      )}
      {row.avg_roi !== undefined && row.avg_roi !== null && (
        <div className="text-white/70">Avg ROI: <span className="text-white">{fmtPct(row.avg_roi)}</span></div>
      )}
    </div>
  );
}

// -------- Liquidity distribution panel (bar chart + table) --------
function LiquidityDistribution({ rows }) {
  const total = rows.reduce((a, b) => a + b.count, 0);
  return (
    <div className="qd-card p-5" data-testid="liq-distribution">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="qd-overline">Liquidity Distribution</div>
          <div className="text-[11px] text-white/45 mt-1">5-band score split · Count · Avg Gap · Avg ROI</div>
        </div>
        <div className="text-[10px] font-mono-data tracking-widest uppercase text-white/40">
          {total} scored
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-5 items-stretch">
        {/* Chart */}
        <div className="h-[200px] w-full min-w-0">
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
            <BarChart data={rows} margin={{ top: 8, right: 8, left: -22, bottom: 4 }}>
              <XAxis dataKey="bucket" tick={{ fill: "#888", fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "#666", fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip cursor={{ fill: "#FF1E1E10" }} content={<BarTooltip />} />
              <Bar dataKey="count" radius={[2, 2, 0, 0]}>
                {rows.map((r) => (
                  <Cell key={r.bucket} fill={LIQ_BUCKET_COLORS[r.bucket] || "#94A3B8"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-xs font-mono-data" data-testid="liq-distribution-table">
            <thead>
              <tr className="text-[10px] tracking-widest uppercase text-white/40 border-b border-[#1A1A1A]">
                <th className="px-2 py-1.5 text-left">Bucket</th>
                <th className="px-2 py-1.5 text-right">Count</th>
                <th className="px-2 py-1.5 text-right">Avg Gap</th>
                <th className="px-2 py-1.5 text-right">Avg ROI</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.bucket}
                  className="border-b border-[#141414]"
                  data-testid={`liq-bucket-row-${r.bucket}`}
                >
                  <td className="px-2 py-1.5">
                    <div className="flex items-center gap-2">
                      <span className="inline-block w-2.5 h-2.5" style={{ background: LIQ_BUCKET_COLORS[r.bucket] }} />
                      <span className="text-white">{r.bucket}</span>
                    </div>
                  </td>
                  <td className="px-2 py-1.5 text-right text-white">{r.count}</td>
                  <td className={`px-2 py-1.5 text-right ${gapClass(r.avg_gap_pct)}`}>{fmtPct(r.avg_gap_pct)}</td>
                  <td className={`px-2 py-1.5 text-right ${r.avg_roi >= 0 ? "text-emerald-400" : "text-[#FF1E1E]"}`}>
                    {fmtPct(r.avg_roi)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// -------- Gap distribution panel (bar chart + table) --------
function GapDistribution({ rows }) {
  const total = rows.reduce((a, b) => a + b.count, 0);
  return (
    <div className="qd-card p-5" data-testid="gap-distribution">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="qd-overline">Gap Distribution</div>
          <div className="text-[11px] text-white/45 mt-1">Offer vs Seller&apos;s Expected · (offer − expected) ÷ expected</div>
        </div>
        <div className="text-[10px] font-mono-data tracking-widest uppercase text-white/40">
          {total} scored
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-5 items-stretch">
        {/* Chart */}
        <div className="h-[200px] w-full min-w-0">
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
            <BarChart data={rows} margin={{ top: 8, right: 8, left: -22, bottom: 4 }}>
              <XAxis dataKey="bucket" tick={{ fill: "#888", fontSize: 9 }} axisLine={false} tickLine={false} interval={0} />
              <YAxis tick={{ fill: "#666", fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip cursor={{ fill: "#FF1E1E10" }} content={<BarTooltip />} />
              <Bar dataKey="count" radius={[2, 2, 0, 0]}>
                {rows.map((r) => (
                  <Cell key={r.bucket} fill={GAP_BUCKET_COLORS[r.bucket] || "#94A3B8"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-xs font-mono-data" data-testid="gap-distribution-table">
            <thead>
              <tr className="text-[10px] tracking-widest uppercase text-white/40 border-b border-[#1A1A1A]">
                <th className="px-2 py-1.5 text-left">Bucket</th>
                <th className="px-2 py-1.5 text-right">Count</th>
                <th className="px-2 py-1.5 text-right">Share</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const pct = total ? (r.count / total) * 100 : 0;
                return (
                  <tr
                    key={r.bucket}
                    className="border-b border-[#141414]"
                    data-testid={`gap-bucket-row-${r.bucket}`}
                  >
                    <td className="px-2 py-1.5">
                      <div className="flex items-center gap-2">
                        <span className="inline-block w-2.5 h-2.5" style={{ background: GAP_BUCKET_COLORS[r.bucket] }} />
                        <span className="text-white">{r.bucket}</span>
                      </div>
                    </td>
                    <td className="px-2 py-1.5 text-right text-white">{r.count}</td>
                    <td className="px-2 py-1.5 text-right text-white/80">{fmtPct(pct)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// -------- Generic table card (Top/Lowest Liquidity, Fast Opportunity, Green Return) --------
// Desktop: tabular; Mobile: card stack — keeps mobile zero-horizontal-scroll.
// Default 5 rows visible; "View More" expands. Spec: "Top 5 leaderboards with View More".
function ListCard({ title, subtitle, rows, columns, testid, emptyHint, mobilePrimary }) {
  const [expanded, setExpanded] = React.useState(false);
  const VISIBLE_DEFAULT = 5;
  const shown = expanded || rows.length <= VISIBLE_DEFAULT ? rows : rows.slice(0, VISIBLE_DEFAULT);
  const moreCount = Math.max(0, rows.length - VISIBLE_DEFAULT);

  return (
    <div className="qd-card p-0 overflow-hidden" data-testid={testid}>
      <div className="px-4 sm:px-5 py-4 border-b border-[#1A1A1A] flex items-baseline justify-between gap-3">
        <div className="min-w-0">
          <div className="qd-overline truncate">{title}</div>
          <div className="text-[11px] text-white/45 mt-1 truncate">{subtitle}</div>
        </div>
        <div className="text-[10px] font-mono-data tracking-widest uppercase text-white/40 shrink-0">
          {rows.length} unit{rows.length === 1 ? "" : "s"}
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="px-5 py-10 text-center text-xs text-white/40 font-mono-data tracking-wide">
          {emptyHint}
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-[10px] font-mono-data tracking-widest uppercase text-white/40 border-b border-[#1A1A1A]">
                  <th className="px-3 py-2 text-left">#</th>
                  {columns.map((c) => (
                    <th key={c.key} className={`px-3 py-2 ${c.align === "right" ? "text-right" : "text-left"}`}>
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {shown.map((r, i) => (
                  <tr
                    key={r.id || i}
                    className="border-b border-[#1A1A1A] hover:bg-white/[0.02]"
                    data-testid={`${testid}-row-${r.id || i}`}
                  >
                    <td className="px-3 py-2 text-[10px] font-mono-data text-white/45 w-10">#{i + 1}</td>
                    {columns.map((c) => (
                      <td key={c.key} className={`px-3 py-2 text-xs ${c.align === "right" ? "text-right font-mono-data" : ""}`}>
                        {c.render(r)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards — stack of the columns. mobilePrimary controls which 2 columns
              get featured in the top row; the rest spread under as a label/value grid. */}
          <div className="md:hidden">
            {shown.map((r, i) => {
              const primaryKeys = mobilePrimary || [columns[0].key, columns[columns.length - 1].key];
              const primary = columns.filter((c) => primaryKeys.includes(c.key));
              const secondary = columns.filter((c) => !primaryKeys.includes(c.key));
              return (
                <div
                  key={r.id || i}
                  data-testid={`${testid}-row-${r.id || i}`}
                  className="border-b border-[#1A1A1A] px-4 py-3"
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] font-mono-data text-white/40 mb-0.5">#{i + 1}</div>
                      {primary[0] && <div className="text-sm">{primary[0].render(r)}</div>}
                    </div>
                    {primary[1] && <div className="text-right shrink-0">{primary[1].render(r)}</div>}
                  </div>
                  {secondary.length > 0 && (
                    <div className="grid grid-cols-3 gap-2 text-[10px] font-mono-data mt-2">
                      {secondary.map((c) => (
                        <div key={c.key}>
                          <div className="text-white/40 uppercase tracking-wider">{c.label}</div>
                          <div>{c.render(r)}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {moreCount > 0 && (
            <button
              type="button"
              onClick={() => setExpanded((e) => !e)}
              data-testid={`${testid}-toggle`}
              className="w-full px-5 py-3 text-[10px] font-mono-data tracking-widest uppercase text-[#FF1E1E] border-t border-[#1A1A1A] hover:bg-[#FF1E1E]/5 transition-colors"
            >
              {expanded ? "Show Top 5" : `View ${moreCount} more`}
            </button>
          )}
        </>
      )}
    </div>
  );
}

// Column packs reused across the four lists.
const VEHICLE_COL = {
  key: "vehicle",
  label: "Vehicle",
  align: "left",
  render: (r) => (
    <div className="min-w-0">
      <div className="text-white font-display normal-case leading-tight truncate max-w-[240px]">
        {r.vehicle || "—"}
      </div>
      {r.registration_number && (
        <div className="text-[10px] font-mono-data text-white/40 uppercase tracking-wider truncate">
          {r.registration_number}
        </div>
      )}
    </div>
  ),
};

export default function ProfitLiquidityIntel() {
  const [scope, setScope] = useState("active");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/admin/analytics/profitability-liquidity?scope=${scope}&limit=20`)
      .then((r) => !cancelled && setData(r.data))
      .catch(() => toast.error("Failed to load profitability intel"))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [scope]);

  if (loading && !data) {
    return (
      <section className="mb-8" data-testid="profit-liquidity-intel">
        <div className="qd-card p-10 text-center text-xs text-white/40 font-mono-data tracking-wide">
          Loading profitability + liquidity intel…
        </div>
      </section>
    );
  }

  return (
    <section className="mb-8" data-testid="profit-liquidity-intel">
      {/* Section header + scope toggle */}
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <div className="qd-overline">Profitability & Liquidity Intelligence</div>
          <div className="text-[11px] text-white/45 mt-1">
            Spot fast deals · concentrate on liquid stock · surface green-return units.
            {data?.scored_with_liquidity !== undefined && (
              <span className="text-white/30 ml-2">
                · {data.scored_with_liquidity} with liquidity · {data.scored_with_gap} with gap
              </span>
            )}
          </div>
        </div>
        <div className="inline-flex border border-[#2A2A2A] bg-[#0E0E0E]" data-testid="pli-scope-tabs">
          {SCOPE_OPTS.map((s) => (
            <button
              key={s.v}
              data-testid={`pli-scope-${s.v}`}
              onClick={() => setScope(s.v)}
              className={`px-3 py-2 text-[10px] font-mono-data tracking-widest uppercase border-r border-[#2A2A2A] last:border-r-0 transition-colors ${
                scope === s.v ? "bg-[#FF1E1E]/15 text-[#FF1E1E]" : "text-white/55 hover:text-white"
              }`}
            >
              {s.l}
            </button>
          ))}
        </div>
      </div>

      {/* Distributions row */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 mb-5">
        <LiquidityDistribution rows={data?.liquidity_distribution || []} />
        <GapDistribution rows={data?.gap_distribution || []} />
      </div>

      {/* Liquidity leaderboards */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 mb-5">
        <ListCard
          title="Top 20 · Highest Liquidity"
          subtitle="Easiest stock to flip · prioritise capital here"
          rows={data?.top_liquidity || []}
          testid="top-liquidity"
          emptyHint="No liquidity-scored vehicles yet. Add liquidity_score on lead workspace."
          columns={[
            VEHICLE_COL,
            { key: "liquidity", label: "Liquidity", align: "right",
              render: (r) => <span className={liquidityBadgeClass(r.liquidity)}>{r.liquidity}/10</span> },
            { key: "gap", label: "Gap %", align: "right",
              render: (r) => <span className={gapClass(r.gap_pct)}>{fmtPct(r.gap_pct)}</span> },
          ]}
        />
        <ListCard
          title="Bottom 20 · Lowest Liquidity"
          subtitle="Slow-movers · de-prioritise / discount aggressively"
          rows={data?.lowest_liquidity || []}
          testid="lowest-liquidity"
          emptyHint="No liquidity-scored vehicles yet."
          columns={[
            VEHICLE_COL,
            { key: "liquidity", label: "Liquidity", align: "right",
              render: (r) => <span className={liquidityBadgeClass(r.liquidity)}>{r.liquidity}/10</span> },
            { key: "gap", label: "Gap %", align: "right",
              render: (r) => <span className={gapClass(r.gap_pct)}>{fmtPct(r.gap_pct)}</span> },
          ]}
        />
      </div>

      {/* Fast Opportunity */}
      <div className="mb-5">
        <ListCard
          title="Fast Opportunity List"
          subtitle="Gap > −5% · seller's ask is at or near our offer · close TODAY"
          rows={data?.fast_opportunity || []}
          testid="fast-opportunity"
          emptyHint="No deals close enough yet. Try widening the offer."
          columns={[
            VEHICLE_COL,
            { key: "liquidity", label: "Liquidity", align: "right",
              render: (r) => <span className={liquidityBadgeClass(r.liquidity)}>
                {r.liquidity === null || r.liquidity === undefined ? "—" : `${r.liquidity}/10`}
              </span> },
            { key: "offer", label: "Offer", align: "right",
              render: (r) => <span className="text-white">{fmtINR(r.offer)}</span> },
            { key: "expected", label: "Seller Ask", align: "right",
              render: (r) => <span className="text-white/70">{fmtINR(r.expected)}</span> },
            { key: "gap", label: "Gap %", align: "right",
              render: (r) => <span className={gapClass(r.gap_pct)}>{fmtPct(r.gap_pct)}</span> },
          ]}
        />
      </div>

      {/* Green Return */}
      <div className="mb-2">
        <ListCard
          title="Green Return List"
          subtitle="Net Profit > 0 · sorted by ROI (highest first)"
          rows={data?.green_return || []}
          testid="green-return"
          emptyHint="No profitable leads yet. Add Estimated Retail Value + offers."
          columns={[
            VEHICLE_COL,
            { key: "gross", label: "Gross", align: "right",
              render: (r) => <span className="text-white">{fmtINR(r.gross_profit)}</span> },
            { key: "net", label: "Net", align: "right",
              render: (r) => <span className="text-emerald-400">{fmtINR(r.net_profit)}</span> },
            { key: "roi", label: "ROI", align: "right",
              render: (r) => <span className="text-emerald-400 font-bold">{fmtPct(r.roi)}</span> },
            { key: "liquidity", label: "Liquidity", align: "right",
              render: (r) => <span className={liquidityBadgeClass(r.liquidity)}>
                {r.liquidity === null || r.liquidity === undefined ? "—" : `${r.liquidity}/10`}
              </span> },
          ]}
        />
      </div>
    </section>
  );
}
